import { DL_BY_DEALER_COUNTRY_API_URL, DL_BY_GEO_CODE_API_URL, DL_BY_GEO_FILTER_API_URL } from '../../constants/dealerLocator.constants';
import { DealerLocatorLandingModel } from './models/dealerLocatorLanding.model';
import { SearchType, TCountry, TFirebaseConfig, TGeometryData, TGeometryDoc, TMapSettings, TState } from './types/dealerLocatorLanding.types';

export const fetchDealersByGeoCode = ({ lat, lng, pageId, language, countryIsoCode }, controllerRef) => {
  if (controllerRef.current) {
    controllerRef.current.abort();
  }

  const controller = new AbortController();
  controllerRef.current = controller;

  const queryUrlParams = new URLSearchParams({
    latitude: lat,
    longitude: lng,
    pageId: pageId,
    language: language,
    country: countryIsoCode,
  });
  const queyrUrl = `${DL_BY_GEO_CODE_API_URL}?${queryUrlParams}`;

  return fetch(queyrUrl, { signal: controllerRef.current?.signal }).then((res) => {
    if (res.ok) {
      return res.json();
    } else {
      console.error(`${res.status} : Failed to fetch dealers from DL API.`);
    }
  });
};

export const fetchCountryDealersByGeoFilter = ({ pageId, language, countryIsoCode, country }, controllerRef) => {
  if (controllerRef.current) {
    controllerRef.current.abort();
  }

  const controller = new AbortController();
  controllerRef.current = controller;

  const queryUrlParams = new URLSearchParams({
    pageId: pageId,
    language: language,
    country: countryIsoCode,
    countryName: country,
  });
  const queyrUrl = `${DL_BY_DEALER_COUNTRY_API_URL}?${queryUrlParams}`;

  return fetch(queyrUrl, { signal: controllerRef.current?.signal }).then((res) => {
    if (res.ok) {
      return res.json();
    } else {
      console.error(`${res.status} : Failed to fetch dealers from DL API.`);
    }
  });
};

export const fetchDealersByGeoFilter = ({ region, pageId, language, countryIsoCode }, controllerRef) => {
  if (controllerRef.current) {
    controllerRef.current.abort();
  }

  const controller = new AbortController();
  controllerRef.current = controller;

  const queryUrlParams = new URLSearchParams({
    state: region,
    pageId: pageId,
    language: language,
    country: countryIsoCode,
  });
  const queyrUrl = `${DL_BY_GEO_FILTER_API_URL}?${queryUrlParams}`;

  return fetch(queyrUrl, { signal: controllerRef.current?.signal }).then((res) => {
    if (res.ok) {
      return res.json();
    } else {
      console.error(`${res.status} : Failed to fetch dealers from DL API.`);
    }
  });
};

export const fetchDataFromFirebaseDB = async ({ app, firebase, collectionName, queryParams }) => {
  if (!app || !collectionName) {
    return null;
  }

  const colSnap = await firebase
    .firestore(app)
    .collection(collectionName)
    .where(queryParams.fieldPath, queryParams.opStr, queryParams.value)
    .get();

  if (!colSnap.empty) {
    const cordsData = colSnap.docs.map((doc) => doc.data());

    return cordsData;
  } else {
    console.error('No such document found!');
    return null;
  }
};

export const initFirebaseService = async (firebaseConfig: TFirebaseConfig) => {
  // Lazy load Firebase Scripts and initialize firebase App. Avoid direct imports of Firebase scripts.
  // Firebase scripts in 8.10.0 version causing Errors during SSR due to side effects in Firebase.
  const firebaseModule = await import('firebase/app');
  await import('firebase/firestore');
  const firebase = await firebaseModule.default;
  
  if(firebase?.apps?.length){
    return { firebase, app: firebase?.apps?.[0] };
  }

  return { firebase, app: firebase.initializeApp(firebaseConfig) };
};

export const isStateType = (obj: TCountry | TState): obj is TState => {
  return Object.hasOwnProperty.call(obj, 'gmiCode');
};

export const isGeometryDocType = (obj: TGeometryData): obj is TGeometryDoc => {
  return Object.hasOwnProperty.call(obj, 'geometry');
};

export const getMapSettingsFromCountry = (country: TCountry | undefined, model: DealerLocatorLandingModel): TMapSettings => {
  if(!country?.latitude || !country?.longitude) {
    return {
      lat: model.initialLatitude,
      lng: model.initialLongitude, 
      zoom: country?.zoom ? Number.parseInt(country?.zoom) : model.initialZoom,
    }
  }

  return {
    lat: Number.parseFloat(country?.latitude),
    lng: Number.parseFloat(country?.longitude), 
    zoom:Number.parseInt(country?.zoom) ?? model.initialZoom,
  }
}

export const getInitialMapSettings = (country: TCountry | null, model: DealerLocatorLandingModel): TMapSettings => {
  const hasSupportedSearchType = model.searchType === SearchType.SearchByCountryAndRegion ||  model.searchType === SearchType.SearchByCountryAndLocationOrDealer;

  if(country){
    return getMapSettingsFromCountry(country, model);
  }

  if(model.hasDefaultCountryPreselected && !model.hasInteractiveMap && hasSupportedSearchType){
    const selectedCountry = model.countries.find(country => country.IsoCode === model.defaultCountry);

    return getMapSettingsFromCountry(selectedCountry, model);
  }

  return {
    lat: model.initialLatitude,
    lng: model.initialLongitude, 
    zoom: model.initialZoom,
  }
}
